import { connect } from 'react-redux';
import { States } from '@core/types';
import Cookies from 'js-cookie';
import * as React from 'react';
import { useCallback } from 'react';
import { MdClose } from 'react-icons/md';

// Global type extensions for OneTrust
declare global {
  interface Window {
    OneTrust?: OnetrustApi;
  }
}

interface OnetrustApi {
  AllowAll: () => void;
  Close: () => void;
  ToggleInfoDisplay: () => void;
  IsAlertBoxClosed: () => boolean;
}

export interface CookiePopupProps {
  oneTrustScriptID?: string;
  title?: string;
  description?: string;
  openCookieSettingsText?: string;
  acceptAllCookiesText?: string;
  onlyUseNecessaryCookiesText?: string | null;
  showClose?: boolean;
  forceAllGreenButtons?: boolean;
}

const POLLING_INTERVAL = 100;

const CookiePopup: React.FC<CookiePopupProps> = (props) => {
  const {
    oneTrustScriptID = '',
    title = '',
    description = '',
    openCookieSettingsText = 'Cookie Settings',
    acceptAllCookiesText = 'Accept All',
    onlyUseNecessaryCookiesText = null,
    showClose = true,
    forceAllGreenButtons = false,
  } = props;

  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const loadOneTrust = useCallback(async (): Promise<OnetrustApi | undefined> => {
    const promise = new Promise<OnetrustApi | undefined>((resolve) => {
      if (!oneTrustScriptID) {
        return resolve(undefined);
      }

      if (window.OneTrust) {
        return resolve(window.OneTrust);
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
      script.type = 'text/javascript';
      script.charset = 'UTF-8';
      script.setAttribute('data-domain-script', oneTrustScriptID);

      script.addEventListener('load', () => {
        const handle = window.setInterval(() => {
          if (window.OneTrust) {
            const element = document.querySelector('#onetrust-consent-sdk');

            if (element) {
              console.log('one trust loaded!', window.OneTrust);
              window.clearInterval(handle);
              return resolve(window.OneTrust);
            }
          }
        }, POLLING_INTERVAL);
      });

      document.head.appendChild(script);
    });

    return promise;
  }, [oneTrustScriptID]);

  const ShowInfoDisplay = async (): Promise<void> => {
    const oneTrust = await loadOneTrust();

    oneTrust?.ToggleInfoDisplay();
  };

  const AcceptAll = async (): Promise<void> => {
    const oneTrust = await loadOneTrust();

    oneTrust?.AllowAll();
  };

  const AcceptNecessary = async (): Promise<void> => {
    const oneTrust = await loadOneTrust();

    oneTrust?.Close();
  };

  React.useEffect(() => {
    // Handle closing our modal when the one trust has been closed / accepted
    const handle = window.setInterval(async () => {
      if (window.OneTrust && window.OneTrust.IsAlertBoxClosed !== undefined && window.OneTrust.IsAlertBoxClosed()) {
        setIsOpen(false);
        window.clearInterval(handle);
      }
    }, POLLING_INTERVAL);
  }, []);

  // Load onetrust on pageload if the cookie has already been accepted
  React.useEffect(() => {
    const oneTrustPopupClosed = !!Cookies.get('OptanonAlertBoxClosed');

    setIsOpen(!oneTrustPopupClosed);

    if (!oneTrustPopupClosed) {
      return;
    }

    loadOneTrust();
  }, [loadOneTrust]);

  const buttonClass = forceAllGreenButtons ? 'btn btn--small btn--green' : 'btn btn--small btn--grey-outline';

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          #onetrust-banner-sdk, .onetrust-pc-dark-filter {
            display: none !important;
          }
        `,
        }}
      />
      <section id="onetrust-cookie-popup" className="cookie-popup" style={{ display: isOpen ? 'flex' : 'none' }}>
        <div
          ref={modalRef}
          className="cookie-popup__modal"
          onMouseEnter={loadOneTrust}
          data-buttons={onlyUseNecessaryCookiesText ? 3 : 2}
        >
          {showClose ? (
            <button id="cookie-onetrust-close" className="cookie-popup__close" onClick={AcceptNecessary} type="button">
              <MdClose />
            </button>
          ) : null}
          <div className="cookie-popup__inner">
            <p className="cookie-popup__title">{title}</p>
            <div className="cookie-popup__description" dangerouslySetInnerHTML={{ __html: description || '' }}></div>
            <div className="cookie-popup__buttons">
              <button
                id="cookie-onetrust-show-info"
                className={`${buttonClass} btn--with-loader`}
                onClick={ShowInfoDisplay}
                type="button"
              >
                <span>{openCookieSettingsText}</span>
              </button>
              {onlyUseNecessaryCookiesText ? (
                <button
                  id="cookie-onetrust-only-necessary"
                  className={`${buttonClass} btn--with-loader`}
                  onClick={AcceptNecessary}
                  type="button"
                >
                  <span>{onlyUseNecessaryCookiesText}</span>
                </button>
              ) : null}
              <button
                id="cookie-onetrust-accept-all"
                className="btn btn--small btn--green btn--with-loader"
                onClick={AcceptAll}
                type="button"
              >
                <span>{acceptAllCookiesText}</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const mapStateToProps = (state: States.IRootState) => ({
  ...state.app.cmsOneTrustConfiguration,
});

export default connect(mapStateToProps)(CookiePopup);
